/**
 * 智能 Agent 分配器
 * 基于任务特征和历史表现智能分配最合适的 Agent
 */

import { ExtendedTask, AgentAssignment } from '../types/extended-task';
import { AgentManager } from './agent-manager';
// TODO: 恢复使用 @gongfu/memory 包
import { AgentMemoryAdapter } from '../utils/memory-stub';
import { logger } from '../utils/logger';
import { BUILTIN_AGENTS } from './builtin-agents';

export interface AllocationOptions {
  enableMemory?: boolean;
  userId?: string;
  projectId?: string;
  preferredAgents?: string[];
  excludeAgents?: string[];
}

export interface AllocationResult {
  primary: string;
  reviewers?: string[];
  consultants?: string[];
  reason?: string;
  confidence?: number;
  memoryBased?: boolean;
}

export class SmartAgentAllocator {
  private agentManager: AgentManager;
  private agentMemory?: AgentMemoryAdapter;
  
  constructor(options?: AllocationOptions) {
    this.agentManager = new AgentManager();
    
    // 初始化 Memory 系统（可选）
    if (options?.enableMemory && options.userId && options.projectId) {
      try {
        this.agentMemory = new AgentMemoryAdapter({
          userId: options.userId,
          projectId: options.projectId,
        });
        logger.info('Memory system initialized for agent allocation');
      } catch (error) {
        logger.warn('Failed to initialize agent memory:', error);
      }
    }
  }
  
  /**
   * 为任务分配最合适的 Agent
   */
  async allocateAgents(task: ExtendedTask): Promise<AllocationResult> {
    logger.info(`Allocating agents for task: ${task.content}`);
    
    // 1. 尝试从记忆中获取推荐
    if (this.agentMemory) {
      try {
        const recommendation = await this.agentMemory.recommendAgent(task.content);
        if (recommendation && recommendation.confidence > 0.7) {
          logger.info(`Using memory-based recommendation: ${recommendation.agentName}`);
          
          const assignment = this.buildAssignmentFromRecommendation(recommendation);
          return {
            ...assignment,
            memoryBased: true,
            confidence: recommendation.confidence,
            reason: recommendation.reason || 'Based on historical performance',
          };
        }
      } catch (error) {
        logger.warn('Failed to get agent recommendation from memory:', error);
      }
    }
    
    // 2. 基于规则的分配
    return this.allocateByRules(task);
  }
  
  /**
   * 基于规则分配 Agent
   */
  private allocateByRules(task: ExtendedTask): AllocationResult {
    const taskType = task.type || 'general';
    const tags = task.tags || [];
    const content = task.content.toLowerCase();
    
    // 根据任务类型分配
    switch (taskType) {
      case 'development':
      case 'feature':
        return this.allocateForDevelopment(task);
        
      case 'bug':
      case 'fix':
        return this.allocateForBugFix(task);
        
      case 'design':
      case 'architecture':
        return this.allocateForDesign(task);
        
      case 'testing':
      case 'test':
        return this.allocateForTesting(task);
        
      case 'documentation':
      case 'docs':
        return this.allocateForDocumentation(task);
        
      case 'analysis':
      case 'research':
        return this.allocateForAnalysis(task);
        
      default:
        return this.allocateDefault(task);
    }
  }
  
  /**
   * 为开发任务分配 Agent
   */
  private allocateForDevelopment(task: ExtendedTask): AllocationResult {
    const content = task.content.toLowerCase();
    const tags = task.tags || [];
    
    // 判断前后端
    const isFrontend = content.includes('前端') || content.includes('ui') || 
                      content.includes('界面') || tags.includes('frontend');
    const isBackend = content.includes('后端') || content.includes('api') || 
                     content.includes('数据库') || tags.includes('backend');
    
    if (isFrontend && !isBackend) {
      return {
        primary: 'agent-frontend',
        reviewers: ['agent-reviewer'],
        consultants: ['agent-architect'],
        reason: 'Frontend development task',
        confidence: 0.85,
      };
    } else if (isBackend && !isFrontend) {
      return {
        primary: 'agent-backend',
        reviewers: ['agent-reviewer'],
        consultants: ['agent-architect'],
        reason: 'Backend development task',
        confidence: 0.85,
      };
    } else {
      // 全栈任务
      return {
        primary: 'agent-backend',
        reviewers: ['agent-frontend', 'agent-reviewer'],
        consultants: ['agent-architect'],
        reason: 'Full-stack development task',
        confidence: 0.8,
      };
    }
  }
  
  /**
   * 为 Bug 修复分配 Agent
   */
  private allocateForBugFix(task: ExtendedTask): AllocationResult {
    // Bug 修复通常需要原开发者和审查者
    return {
      primary: 'agent-backend', // 默认后端，实际应根据 bug 位置
      reviewers: ['agent-tester', 'agent-reviewer'],
      reason: 'Bug fix requires testing and review',
      confidence: 0.75,
    };
  }
  
  /**
   * 为设计任务分配 Agent
   */
  private allocateForDesign(task: ExtendedTask): AllocationResult {
    return {
      primary: 'agent-architect',
      reviewers: ['agent-reviewer'],
      consultants: ['agent-backend', 'agent-frontend'],
      reason: 'Design and architecture task',
      confidence: 0.9,
    };
  }
  
  /**
   * 为测试任务分配 Agent
   */
  private allocateForTesting(task: ExtendedTask): AllocationResult {
    return {
      primary: 'agent-tester',
      reviewers: ['agent-reviewer'],
      consultants: ['agent-backend', 'agent-frontend'],
      reason: 'Testing task',
      confidence: 0.9,
    };
  }
  
  /**
   * 为文档任务分配 Agent
   */
  private allocateForDocumentation(task: ExtendedTask): AllocationResult {
    return {
      primary: 'agent-documenter',
      reviewers: ['agent-reviewer'],
      reason: 'Documentation task',
      confidence: 0.9,
    };
  }
  
  /**
   * 为分析任务分配 Agent
   */
  private allocateForAnalysis(task: ExtendedTask): AllocationResult {
    return {
      primary: 'agent-product',
      reviewers: ['agent-architect'],
      consultants: ['agent-backend', 'agent-frontend'],
      reason: 'Analysis and research task',
      confidence: 0.8,
    };
  }
  
  /**
   * 默认分配
   */
  private allocateDefault(task: ExtendedTask): AllocationResult {
    // 根据优先级分配
    if (task.priority === 'urgent' || task.priority === 'high') {
      return {
        primary: 'agent-backend',
        reviewers: ['agent-reviewer'],
        reason: 'High priority task, assigned to experienced agent',
        confidence: 0.6,
      };
    }
    
    return {
      primary: 'agent-developer',
      reviewers: ['agent-reviewer'],
      reason: 'General task assignment',
      confidence: 0.5,
    };
  }
  
  /**
   * 从推荐结果构建分配
   */
  private buildAssignmentFromRecommendation(recommendation: any): AllocationResult {
    const agent = BUILTIN_AGENTS.find(a => a.name === recommendation.agentName);
    if (!agent) {
      return this.allocateDefault({} as ExtendedTask);
    }
    
    // 根据 Agent 能力添加合适的审查者
    const reviewers: string[] = ['agent-reviewer'];
    const consultants: string[] = [];
    
    if (agent.capabilities.includes('architecture')) {
      consultants.push('agent-architect');
    }
    
    if (agent.capabilities.includes('testing')) {
      reviewers.push('agent-tester');
    }
    
    return {
      primary: recommendation.agentName,
      reviewers,
      consultants: consultants.length > 0 ? consultants : undefined,
    };
  }
  
  /**
   * 记录分配结果（用于学习）
   */
  async recordAllocation(
    task: ExtendedTask, 
    allocation: AllocationResult,
    outcome?: 'success' | 'failure' | 'reassigned'
  ): Promise<void> {
    if (!this.agentMemory) return;
    
    try {
      // 记录主要执行者的表现
      if (allocation.primary) {
        await this.agentMemory.recordAgentPerformance(
          allocation.primary,
          task.content,
          {
            taskId: task.id,
            outcome: outcome || 'pending',
            duration: task.completedAt && task.startedAt
              ? new Date(task.completedAt).getTime() - new Date(task.startedAt).getTime()
              : undefined,
            quality: outcome === 'success' ? 1 : 0,
          }
        );
      }
      
      logger.info(`Recorded allocation outcome for task ${task.id}`);
    } catch (error) {
      logger.error('Failed to record allocation:', error);
    }
  }
  
  /**
   * 获取 Agent 分配统计
   */
  async getAllocationStats(): Promise<any> {
    if (!this.agentMemory) {
      return { message: 'Memory system not enabled' };
    }
    
    try {
      const agents = BUILTIN_AGENTS.map(a => a.name);
      const stats: any = {};
      
      for (const agentName of agents) {
        const performance = await this.agentMemory.getAgentPerformance(agentName);
        if (performance) {
          stats[agentName] = {
            totalTasks: performance.totalTasks,
            successRate: performance.successRate,
            averageTime: performance.averageTime,
            specialties: performance.specialties,
          };
        }
      }
      
      return stats;
    } catch (error) {
      logger.error('Failed to get allocation stats:', error);
      return { error: error.message };
    }
  }
}